import { useEffect, useMemo, useState } from 'react';
import BumbleTabs from '@bumble/app/components/BumbleTabs';
import CreateModal from '@bumble/app/components/CreateModal';
import DataTableGrid from '@bumble/app/components/DataTableGrid';
import DialogConfirm from '@bumble/app/components/DialogConfirm';
import DialogStandard from '@bumble/app/components/DialogStandard';
import ManageFormFactor from '@bumble/app/components/ManageFormFactor';
import { Loading } from '@bumble/app/components/Loading';
import { Indicator } from '@bumble/app/components/Indicator';
import MultipleSelections from '@bumble/app/components/MultipleSelections';
import { NodeHealthTest } from '@bumble/app/components/NodeHealthTest';
import NodeMetrics from '@bumble/app/components/NodeMetrics';
import PageContent from '@bumble/app/components/PageContent';
import PageHeader from '@bumble/app/components/PageHeader';
import SplitPanes from '@bumble/app/components/SplitPanes';
import {
  EndpointNodeCreate,
  EndpointNodeTabDetails,
  EndpointNodeTabEndpoint,
  EndpointNodeLanInterface,
} from '@bumble/app/components/endpoint-node';
import { SecondaryIps } from '@bumble/app/components/endpoint/SecondaryIps';
import { useApi } from '@bumble/app/hooks/use-api';
import { apiError } from '@bumble/app/utils/api-error';
import { Button, Card, CardContent } from '@mui/material';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import { useMutation, useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { toast } from 'react-toastify';
import AddLocationAltOutlinedIcon from '@mui/icons-material/AddLocationAltOutlined';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import LanOutlinedIcon from '@mui/icons-material/LanOutlined';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import RoomPreferencesOutlinedIcon from '@mui/icons-material/RoomPreferencesOutlined';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import { NavLink } from 'react-router-dom';

const columns: GridColDef[] = [
  {
    field: 'ep_node_name',
    headerName: 'Endpoint Node Name',
    flex: 1,
    minWidth: 200,
    disableColumnMenu: true,
  },
  {
    field: 'ep_node_id',
    headerName: 'Ep Node Id',
    width: 200,
    align: 'left',
    disableColumnMenu: true,
  },
  {
    field: 'ep_node_group_id',
    headerName: 'Node Group Id',
    flex: 1,
    minWidth: 200,
    disableColumnMenu: true,
  },
  {
    field: 'admin_state',
    headerName: 'Admin State',
    width: 120,
    disableColumnMenu: true,
  },
  {
    field: 'registered_at',
    headerName: 'Registered at',
    width: 200,
    disableColumnMenu: true,
  },
  {
    field: 'op_state',
    headerName: 'Op State',
    width: 120,
    disableColumnMenu: true,
    renderCell: (params) => {
      return params.value !== undefined ? <Indicator active={params.value === 'up'} /> : 'N/A';
    }
  },
  {
    field: 'primary_ip',
    headerName: 'Primary IP',
    width: 120,
    disableColumnMenu: true,
  },
  {
    field: 'form_factor',
    headerName: 'Form Factor',
    width: 120,
    disableColumnMenu: true,
  },
  {
    field: 'created_at',
    headerName: 'Created at',
    minWidth: 150,
    headerAlign: 'right',
    align: 'right',
    disableColumnMenu: true,
    valueGetter: (params) => {
      if (params.value === null) return '';
      return DateTime.fromISO(params.value).toLocaleString(DateTime.DATETIME_SHORT);
    },
  },
];

const columnVisible = {
  ep_node_name: true,
  ep_node_id: true,
  ep_node_group_id: true,
  primary_ip: true,
  registered: true,
  registered_at: true,
  created_at: true,
};

export const EndpointNodes = () => {
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);
  const handleSelectionChange = (newSelection: GridRowSelectionModel) => {
    setRowSelectionModel(newSelection);
  };
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const handleCreateModalOpen = () => {
    setOpenCreateModal(true);
  };

  const [openFormFactorModal, setOpenFormFactorModal] = useState(false);
  const [openIpModal, setOpenIpModal] = useState(false);

  const [selectedItemId, setSelectedItemId] = useState<string>('');
  const selectedRowItem = useMemo(() => {
    return rowSelectionModel.length === 1 ? rowSelectionModel[0].toString() : '';
  }, [rowSelectionModel]);
  useEffect(() => {
    if (rowSelectionModel.length === 1) {
      setSelectedItemId(selectedRowItem);
    } else {
      setSelectedItemId('');
    }
  }, [rowSelectionModel, selectedRowItem]);

  const api = useApi();
  const { isLoading, data, refetch } = useQuery(
    ['endpointNodes'],
    api.listEpNodeApiV1ListEndpointNodeGet.bind(null, {}),
    { enabled: true },
  );

  const [showConfirmDelete, setShowConfirmDelete] = useState<boolean>(false);
  const [showHealthDialog, setShowHealthDialog] = useState<boolean>(false);
  const [showLanDialog, setShowLanDialog] = useState<boolean>(false);

  const handleDataRefetch = () => {
    refetch();
    toast.success('Refreshed Endpoint Nodes.');
  };

  const removeEndpointNode = useMutation(api.removeEpNodeApiV1RemoveEndpointNodePost, {
    onSuccess() {
      toast.success('Endpoint Node removed.');
      handleDataRefetch();
    },
    onError: apiError,
  });

  const handleEndpointNodeDelete = () => {
    const selectedRowData = data?.data.filter((ep) => ep.ep_node_id === selectedItemId);
    if (selectedRowData && selectedRowData.length === 1 && selectedRowData[0]?.ep_node_group_id) {
      removeEndpointNode.mutate({
        ep_node_id: selectedItemId,
        ep_node_group_id: selectedRowData[0]?.ep_node_group_id,
      });
    }
  };

  const handleIpsSubmit = () => {
    setOpenIpModal(false);
    handleDataRefetch();
  };

  const handleCreateModalClose = () => {
    setOpenCreateModal(false);
    handleDataRefetch();
  };

  const handleLanSubmit = () => {
    setShowLanDialog(false);
    handleDataRefetch();
  };

  const {
    isLoading: detailLoading,
    data: detailData,
    refetch: detailRefetch,
  } = useQuery(
    ['appServiceDetail'],
    api.getEndpointNodeDetailApiV1GetEndpointNodeDetailGet.bind(null, {
      ep_node_id: selectedRowItem,
    }),
    { enabled: false, cacheTime: 0 },
  );

  useEffect(() => {
    if (selectedRowItem) {
      detailRefetch();
    }
  }, [detailRefetch, selectedRowItem]);

  return (
    <PageContent>
      <PageHeader pageName="Endpoint Nodes" count={data?.data?.length || 0}
        actions={
          <Button
            variant="text"
            startIcon={<FolderOutlinedIcon fontSize="small" />}
            component={NavLink}
            to="/endpoint-node-groups"
          >
            Manage Endpoint Node Groups
          </Button>
        }

      />
      {isLoading ? (
        <Loading />
      ) : (
        <SplitPanes
          top={
            <Card elevation={0}>
              <CardContent>
                <DataTableGrid
                  rows={
                    data?.data.map((item) => {
                      return {
                        ...item,
                        id: item.ep_node_id,
                      };
                    }) || []
                  }
                  columns={columns}
                  handleSelectionChange={handleSelectionChange}
                  rowSelectionModel={rowSelectionModel}
                  itemsSelected={rowSelectionModel.length === 1}
                  newLabel="Create Endpoint Node"
                  newOnClick={handleCreateModalOpen}
                  columnVisibilityModel={columnVisible}
                  // getRowId={(row: EpNode) => row.ep_node_id}
                  refreshData={handleDataRefetch}
                  menuItems={[
                    {
                      label: 'Manage Form Factor',
                      icon: <RoomPreferencesOutlinedIcon color="info" fontSize="small" />,
                      onClick: () => setOpenFormFactorModal(true),
                    },
                    {
                      label: 'Add Secondary IPs',
                      icon: <AddLocationAltOutlinedIcon color="info" fontSize="small" />,
                      onClick: () => setOpenIpModal(true),
                    },
                    {
                      label: 'Test Node Health',
                      icon: <MonitorHeartOutlinedIcon color="info" fontSize="small" />,
                      onClick: () => setShowHealthDialog(true),
                    },
                    {
                      label: 'Configure LAN Interface',
                      icon: <LanOutlinedIcon color="info" fontSize="small" />,
                      onClick: () => setShowLanDialog(true),
                    },
                    {
                      label: 'Delete',
                      icon: <DeleteForeverOutlinedIcon color="error" fontSize="small" />,
                      onClick: () => setShowConfirmDelete(true),
                      hasDivider: true,
                    },
                  ]}
                />
              </CardContent>
            </Card>
          }
          bottom={
            <Card elevation={0} sx={{ mt: 5 }}>
              <CardContent>
                {rowSelectionModel.length === 1 ? (
                  <BumbleTabs
                    tabs={[
                      {
                        el: (
                          <EndpointNodeTabDetails data={detailData?.data} loading={detailLoading} />
                        ),
                        label: 'Details',
                        id: 0,
                      },
                      {
                        el: (
                          <EndpointNodeTabEndpoint
                            data={detailData?.data}
                            loading={detailLoading}
                          />
                        ),
                        label: 'Endpoints',
                        id: 1,
                      },
                      {
                        el: <NodeMetrics id={selectedRowItem} />,
                        label: 'Monitoring',
                        id: 2,
                      },
                    ]}
                  />
                ) : (
                  <MultipleSelections count={rowSelectionModel.length} />
                )}
              </CardContent>
            </Card>
          }
        />
      )}
      {openCreateModal && (
        <CreateModal
          open={openCreateModal}
          handleClose={handleCreateModalClose}
          title="Create Endpoint Node"
        >
          <EndpointNodeCreate onCancel={handleCreateModalClose} />
        </CreateModal>
      )}
      {openFormFactorModal && selectedItemId && (
        <DialogStandard
          open={openFormFactorModal}
          handleClose={() => setOpenFormFactorModal(false)}
          title="Manage Form Factor"
        >
          <ManageFormFactor type="endpoint" selectedNode={selectedItemId} onClose={() => setOpenFormFactorModal(false)} />
        </DialogStandard>
      )}
      {openIpModal && selectedItemId && (
        <DialogStandard
          open={openIpModal}
          handleClose={() => setOpenIpModal(false)}
          title="Add Secondary IPs"
          info="https://www.bumblebeenet.com/post/how-to-add-more-endpoints-on-an-endpoint-node"
        >
          <SecondaryIps
            endpointNode={selectedItemId}
            ips={detailData?.data?.secondary_ips || ''}
            onSubmit={handleIpsSubmit}
          />
        </DialogStandard>
      )}
      {showLanDialog && selectedItemId && (
        <DialogStandard
          open={showLanDialog}
          handleClose={() => setShowLanDialog(false)}
          title="Configure LAN Interface"
        >
          <EndpointNodeLanInterface
            endpointNode={selectedItemId}
            onSubmit={handleLanSubmit}
            endpointLanEnabled={detailData?.data?.lan_interface_enabled || false}
            endpointLan={detailData?.data?.lan_interface_ipaddress}
          />
        </DialogStandard>
      )}
      {showConfirmDelete && (
        <DialogConfirm
          open={showConfirmDelete}
          handleClose={() => setShowConfirmDelete(false)}
          handleConfirm={handleEndpointNodeDelete}
          title="Delete Endpoint Node?"
          description={`Are you certain you wish to proceed with deleting ${selectedItemId}? Please note that this action is irreversible and will result in the permanent removal of the endpoint node.`}
          confirmationPhrase="confirm"
        />
      )}
      {showHealthDialog && selectedItemId && detailData && (
        <DialogStandard
          open={showHealthDialog}
          handleClose={() => setShowHealthDialog(false)}
          title="Node Health Check"
          size="md"
        >
          <NodeHealthTest nodeId={selectedItemId} />
        </DialogStandard>
      )}
    </PageContent>
  );
};
